export type BrandKey = 'luigis' | 'antigreed' | 'ahp';

export interface BrandConfig {
  key: BrandKey;
  name: string;
  shortName: string;
  tagline: string;
  description: string;
  domain: string;
  primaryDomain: string;
  /**
   * Per-brand API origin (Supabase / PostgREST + auth + storage).
   * Used as the runtime fallback when VITE_SUPABASE_URL is unset so the
   * three integrations/supabase/* clients resolve correctly under each
   * brand build instead of all falling back to luigi's gateway.
   */
  apiUrl: string;
  /**
   * Per-brand app-icon URL displayed in floating-button / mascot slots.
   * Root-relative (served from each brand's /public/) so the same path
   * works under dev, preview, and prod without further env wiring.
   */
  appIconUrl: string;
  // Visual branding
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    background: string;
  };
  // Content variations
  messaging: {
    heroTitle: {
      primary: string;
      secondary: string;
    };
    heroSubtitle: string;
    heroDescription: string;
    corruptionTitle: string;
    corruptionDescription: string;
    callToAction: string;
    navButtons: {
      dataVault: string;
      hospitalMap: string;
      info: string;
    };
  };
  // SEO & Meta
  seo: {
    title: string;
    titleTemplate: string; // %s will be replaced with page title
    description: string;
    keywords: string[];
    author: string;
  };
  // Social media
  social: {
    twitter?: string;
    ogImage: string;
  };
  // Legal/Footer
  legal: {
    organization: string;
    copyright: string;
  };
  // Contact
  contact: {
    support: string;
  };
}

export const BRANDS: Record<BrandKey, BrandConfig> = {
  luigis: {
    key: 'luigis',
    name: "Luigi's Hall of Shame",
    shortName: "Luigi's",
    tagline: "Exposing Healthcare Price Gouging Across America",
    description: "Healthcare Price Transparency Platform exposing price gouging across America",
    domain: "luigishallofshame.com",
    primaryDomain: "https://luigishallofshame.com",
    apiUrl: "https://api.luigishallofshame.com",
    // No brand-prefixed icon in /public/ for luigi yet — the existing
    // apple-touch-icon.png is the luigi-branded default. Swap to
    // /luigis-icon-1024.png once that asset lands.
    appIconUrl: "/apple-touch-icon.png",
    colors: {
      primary: "#00ff00", // matrix-green
      secondary: "#ff0000", // blood-red
      accent: "#00ffff", // terminal-green
      text: "#ffffff",
      background: "#000000"
    },
    messaging: {
      heroTitle: {
        primary: "Luigi's",
        secondary: "Hall of Shame"
      },
      heroSubtitle: "Exposing Healthcare Price Gouging Across America",
      heroDescription: "A digital rebellion against the broken healthcare system. Real hospital prices. Real international comparisons. Real corruption exposed.",
      corruptionTitle: "Real Corruption Exposed",
      corruptionDescription: "The healthcare pricing matrix is compromised. Citizens are being systematically overcharged.",
      callToAction: "Enter The Data Vault",
      navButtons: {
        dataVault: "Data Vault",
        hospitalMap: "Hospital Map",
        info: "Info/Donate"
      }
    },
    seo: {
      title: "Luigi's Hall of Shame - Healthcare Price Transparency",
      titleTemplate: "%s | Luigi's Hall of Shame",
      description: "Exposing healthcare price gouging across America. Compare hospital prices internationally and see real chargemaster data corruption.",
      keywords: [
        "healthcare price transparency",
        "hospital chargemaster",
        "medical price gouging",
        "healthcare costs",
        "hospital pricing",
        "medical bills",
        "luigi's hall of shame"
      ],
      author: "Luigi's Hall of Shame"
    },
    social: {
      twitter: "@lovable_dev",
      ogImage: "https://luigishallofshame.com/og-image.jpg"
    },
    legal: {
      organization: "Luigi's Hall of Shame",
      copyright: "Luigi's Hall of Shame"
    },
    contact: {
      support: "support@luigishallofshame.com"
    }
  },

  antigreed: {
    key: 'antigreed',
    name: "AntiGreed.app",
    shortName: "AntiGreed",
    tagline: "Fighting Corporate Healthcare Greed",
    description: "Corporate healthcare accountability platform fighting medical price manipulation and corporate greed",
    domain: "antigreed.app",
    primaryDomain: "https://antigreed.app",
    apiUrl: "https://api.antigreed.app",
    appIconUrl: "/antigreed-icon.png",
    colors: {
      primary: "#f5e6d3", // cream/beige
      secondary: "#ff6600", // warning-orange
      accent: "#e8d4b0", // light-cream
      text: "#f5e6d3", // cream text
      background: "#0a1408" // very dark green background
    },
    messaging: {
      heroTitle: {
        primary: "Anti",
        secondary: "Greed"
      },
      heroSubtitle: "Your guide to surviving late stage capitalism",
      heroDescription: "Exposing corporate healthcare manipulation and price fixing. Real data. Real accountability. Real change for patients.",
      corruptionTitle: "Corporate Greed Exposed",
      corruptionDescription: "Healthcare corporations are systematically extracting wealth from sick Americans. We're exposing their greed.",
      callToAction: "Expose The Greed",
      navButtons: {
        dataVault: "Price Database",
        hospitalMap: "Corporate Map",
        info: "About/Support"
      }
    },
    seo: {
      title: "AntiGreed.app - Fighting Corporate Healthcare Greed",
      titleTemplate: "%s | AntiGreed.app",
      description: "Fighting corporate healthcare greed and price manipulation. Exposing hospital price gouging and corporate medical profiteering.",
      keywords: [
        "corporate healthcare greed",
        "medical price manipulation",
        "hospital profiteering",
        "healthcare accountability",
        "corporate greed",
        "medical price fixing",
        "antigreed app"
      ],
      author: "AntiGreed.app"
    },
    social: {
      twitter: "@antigreed_app",
      ogImage: "https://antigreed.app/og-image.jpg"
    },
    legal: {
      organization: "VICTIMS OF LATE STAGE CAPITALISM, LLC.",
      copyright: "VICTIMS OF LATE STAGE CAPITALISM, LLC."
    },
    contact: {
      support: "support@antigreed.app"
    }
  },

  ahp: {
    key: 'ahp',
    name: "American Hospital Prices",
    shortName: "AHP",  // American Hospital Prices
    tagline: "Know Your Price. Know Your Rights.",
    description: "America's hospital price transparency platform helping every American compare healthcare costs",
    domain: "americanhospitalprices.com",
    primaryDomain: "https://americanhospitalprices.com",
    apiUrl: "https://api.americanhealthprices.com",
    appIconUrl: "/ahp-icon-1024.png",
    colors: {
      primary: "#B22234", // Old Glory Red
      secondary: "#002868", // Old Glory Blue (navy)
      accent: "#C5A55A", // Presidential gold
      text: "#1B1B1B", // near-black
      background: "#FAF9F6" // warm white
    },
    messaging: {
      heroTitle: {
        primary: "American Hospital",
        secondary: "Prices"
      },
      heroSubtitle: "Know Your Price. Know Your Rights.",
      heroDescription: "Compare real hospital prices near you. Every American deserves to know what healthcare costs before they get the bill.",
      corruptionTitle: "Price Transparency Matters",
      corruptionDescription: "Hospitals are required by law to publish their prices. We make those prices easy to find, compare, and understand.",
      callToAction: "Search Hospital Prices",
      navButtons: {
        dataVault: "Price Search",
        hospitalMap: "Hospital Map",
        info: "About"
      }
    },
    seo: {
      title: "American Hospital Prices - Compare Real Hospital Costs Near You",
      titleTemplate: "%s | American Hospital Prices",
      description: "Compare real hospital prices near you. Free hospital price comparison tool for every American. Search by procedure, ZIP code, or hospital name.",
      keywords: [
        "hospital prices",
        "healthcare costs",
        "hospital price comparison",
        "medical procedure costs",
        "hospital price transparency",
        "compare hospital prices",
        "american health prices"
      ],
      author: "American Hospital Prices"
    },
    social: {
      twitter: "@amhealthprices",
      ogImage: "https://americanhospitalprices.com/consumer/og-image.png"
    },
    legal: {
      organization: "American Hospital Prices",
      copyright: "American Hospital Prices"
    },
    contact: {
      support: "support@americanhospitalprices.com"
    }
  }
};

export const DEFAULT_BRAND: BrandKey = 'luigis';

// Brand detection utilities
export const detectBrandFromDomain = (hostname: string): BrandKey => {
  // Handle localhost and development
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    if (hostname.includes('antigreed') || process.env.VITE_BRAND === 'antigreed') {
      return 'antigreed';
    }
    if (hostname.includes('ahp') || process.env.VITE_BRAND === 'ahp') {
      return 'ahp';
    }
    return DEFAULT_BRAND;
  }

  // Production domain detection. After the 2026-06-14 migration to
  // americanhospitalprices.com, both hostnames must match — the new domain is
  // canonical, the old domain still serves under the 301 redirect (the
  // redirect is in functions/_middleware.ts, but the SPA still runs for any
  // path the middleware passes through, so brand detection must cover both).
  if (
    hostname.includes('americanhospitalprices.com') ||
    hostname.includes('americanhealthprices.com') ||
    hostname.includes('ahp')
  ) {
    return 'ahp';
  }

  if (hostname.includes('antigreed.app') || hostname.includes('antigreed')) {
    return 'antigreed';
  }

  if (hostname.includes('luigishallofshame.com') || hostname.includes('luigis')) {
    return 'luigis';
  }

  return DEFAULT_BRAND;
};

// Build-time brand detection for static generation
export const getBuildTimeBrand = (): BrandKey => {
  const buildBrand = process.env.VITE_BRAND as BrandKey;
  if (buildBrand && BRANDS[buildBrand]) {
    return buildBrand;
  }
  return DEFAULT_BRAND;
};

export const getBrand = (hostname?: string): BrandConfig => {
  let brandKey: BrandKey;

  if (typeof window !== 'undefined' && !hostname) {
    // Client-side detection
    brandKey = detectBrandFromDomain(window.location.hostname);
  } else if (hostname) {
    // Server-side or explicit hostname
    brandKey = detectBrandFromDomain(hostname);
  } else {
    // Build-time or fallback
    brandKey = getBuildTimeBrand();
  }

  return BRANDS[brandKey];
};

// Hook for React components
export const useBrand = (): BrandConfig => {
  // For static builds, we need to use build-time detection
  const brandKey = typeof window !== 'undefined'
    ? detectBrandFromDomain(window.location.hostname)
    : getBuildTimeBrand();

  return BRANDS[brandKey];
};